import { Button, ButtonTypes, Input, type SelectOption, Select } from "@clintonelec/react-storybook";
import { type Dispatch, memo, type SetStateAction } from "react";
import type { EmailFtpSettingsState } from "Interfaces";
import { produce } from "immer";
import "Components/ConfigLayout/Event/EmailFtpSettings/EmailServerSettings/EmailServerSettings.less";

const securityOptions: SelectOption[] = [
	{
		label: "Off",
		value: "OFF"
	},
	{
		label: "SSL",
		value: "SSL"
	},
	{
		label: "TLS/Start TLS",
		value: "TLS"
	}
];

type EmailServerSettingsProps = {
	localEmailFtpState: [ EmailFtpSettingsState, Dispatch<SetStateAction<EmailFtpSettingsState>> ];
};

function EmailServerSettings(props: EmailServerSettingsProps) {
	const { localEmailFtpState: [ localEmailFtpSettings, setLocalEmailFtpSettings ] } = props;
	const { server, password, port, security, user, testEmailAddress } = localEmailFtpSettings.emailServerSettings;

	const handleServerUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, draft => {
			draft.emailServerSettings.server = value;
		}));
	};

	const handlePortUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, draft => {
			draft.emailServerSettings.port = +value;
		}));
	};

	const handleSecuritySelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalEmailFtpSettings(produce(localEmailFtpSettings, draft => {
			draft.emailServerSettings.security = value;
		}));
	};

	const handleUserUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, draft => {
			draft.emailServerSettings.user = value;
		}));
	};

	const handleTestEmailAddressUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, draft => {
			draft.emailServerSettings.testEmailAddress = value;
		}));
	};

	const handlePasswordUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.emailServerSettings.password = value;
		}));
	};

	return (
		<div className="email-server-settings">
			<div className="header">
				<h4>Email Setup</h4>
			</div>
			<div className="form-row">
				<span>Server</span>
				<Input
					onUpdate={ handleServerUpdate }
					value={ server }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<span>Port</span>
				<Input
					hideCharMessage={ true }
					max={ 65535 }
					min={ 1 }
					onUpdate={ handlePortUpdate }
					type="number"
					value={ port }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<span>Security</span>
				<Select
					allowClear={ false }
					className="form-select"
					onSelect={ handleSecuritySelect }
					options={ securityOptions }
					value={ security }
				/>
			</div>
			<div className="form-row">
				<span>User</span>
				<Input
					onUpdate={ handleUserUpdate }
					type="email"
					value={ user }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<span>Password</span>
				<Input
					onUpdate={ handlePasswordUpdate }
					password={ true }
					placeholder="********"
					value={ password }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<span>Test E-mail Address</span>
				<Input
					onUpdate={ handleTestEmailAddressUpdate }
					type="email"
					value={ testEmailAddress }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<div className="form-button">
					<Button htmlType="button" type={ ButtonTypes.TERTIARY }>Test E-mail</Button>
				</div>
			</div>
		</div>
	);
}

export default memo(EmailServerSettings);
